import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useContext, useMemo } from "react";
import { SessionContext } from "../Contexts/SessionContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IProps {
    dates: Date[] | null;
    loading: boolean;
    startDate: Date;
    endDate: Date;
}

export default function SurveyDistributionChart({ dates, loading, startDate,endDate }: IProps) {
    const { sessionState } = useContext(SessionContext);

    const start = sessionState.landusePage.startDate;
    const end = sessionState.landusePage.endDate;
    const chartHeight = { xs: 300, md: 360 };

    const data = useMemo(() => {
        if (!dates || dates.length === 0) {
            return {
                labels: [],
                datasets: [
                    {
                        label: "Number of Surveys",
                        data: [],
                        backgroundColor: [],
                    },
                ],
            };
        }

        const counts = dates
            .map((d) => new Date(d).getFullYear())
            .reduce((acc, y) => {
                acc[y] = (acc[y] || 0) + 1;
                return acc;
            }, {} as Record<number, number>);

        const minYear = startDate.getFullYear();//   Math.min(...Object.keys(counts).map(Number));
        const maxYear = endDate.getFullYear();//   Math.max(...Object.keys(counts).map(Number));

        const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
        const surveyCounts = years.map((y) => counts[y] || 0);

        const startYear = start ? start.year() : null;
        const endYear = end ? end.year() : null;

        const backgroundColors = years.map((year) => {
            if (
                startYear !== null &&
                endYear !== null &&
                (year < startYear || year > endYear)
            ) {
                return "rgba(150, 150, 150, 0.4)"; // greyed out
            }
            return "rgba(54, 162, 235, 0.7)"; // blue
        });

        return {
            labels: years.map(String),
            datasets: [
                {
                    label: "Number of Surveys",
                    data: surveyCounts,
                    backgroundColor: backgroundColors
                },
            ],
        };
    }, [dates, end, endDate, start, startDate]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
        },
        scales: {
            x: {
                ticks: { autoSkip: false, maxRotation: 60, minRotation: 0 },
                title: { display: true }
            },
            y: { beginAtZero: true, title: { display: true, text: "Number of Surveys" } },
        },
    };

    return (
        <Grid size={12} sx={{ height: chartHeight, mb: -3 }}>
            {loading ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    gap={1}
                >
                    <CircularProgress />
                    <Typography color="text.secondary">Loading survey dates...</Typography>
                </Box>
            ) : !dates || dates.length === 0 ? (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color="text.secondary">No survey data available</Typography>
                </Box>
            ) : (
                <Box height="100%">
                    <Bar data={data} options={options} />
                </Box>
            )}
        </Grid>
    );
}
